// Small, dependency-free SVG/HTML chart primitives used by the planning console.
// Colorful and compact; all sizing is responsive (viewBox / % widths).

import type { ReactNode } from "react";

/** Compact number label: 1.2M / 34K / 850. */
export function kFmt(n: number): string {
  const v = Math.round(n);
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(Math.abs(v) >= 10000 ? 0 : 1)}K`;
  return String(v);
}

const full = (n: number) => Math.round(n).toLocaleString("en-US");

/**
 * Vertical bars with value labels, optional per-bar color, a highlighted bar,
 * optional per-bar delta (e.g. WoW %) and an optional trend line across the tops.
 */
export function BarsChart({
  data,
  height = 200,
  highlightIndex,
  showLine,
  lineLabel,
}: {
  data: Array<{ label: string; value: number; color?: string; sub?: string; delta?: string; deltaTone?: "up" | "down" | "flat" }>;
  height?: number;
  highlightIndex?: number;
  showLine?: boolean;
  lineLabel?: string;
}) {
  const W = 1120;
  const padL = 8;
  const padR = 8;
  const padT = 22;
  const padB = 30;
  const plotW = W - padL - padR;
  const plotH = height - padT - padB;
  const max = Math.max(1, ...data.map((d) => d.value));
  const slot = plotW / Math.max(1, data.length);
  const barW = Math.min(70, slot * 0.6);
  const baseY = padT + plotH;
  const scale = (v: number) => (v / max) * plotH;
  const cxOf = (i: number) => padL + i * slot + slot / 2;
  const deltaColor = (t?: string) => (t === "up" ? "#059669" : t === "down" ? "#dc2626" : "#94a3b8");
  return (
    <svg viewBox={`0 0 ${W} ${height}`} className="w-full" role="img" aria-label="bar chart">
      {[0.25, 0.5, 0.75, 1].map((f) => (
        <line key={f} x1={padL} y1={padT + plotH * (1 - f)} x2={W - padR} y2={padT + plotH * (1 - f)} stroke="#f1f5f9" />
      ))}
      <text x={padL} y={padT - 8} className="fill-slate-300 text-[9px] tabular-nums">{kFmt(max)}</text>
      {showLine && lineLabel && <text x={W - padR} y={padT - 8} textAnchor="end" className="fill-slate-400 text-[9px]">{lineLabel}</text>}
      <line x1={padL} y1={baseY} x2={W - padR} y2={baseY} stroke="#cbd5e1" />
      {data.map((d, i) => {
        const cx = cxOf(i);
        const h = scale(d.value);
        return (
          <g key={i}>
            {i === highlightIndex && <rect x={padL + i * slot} y={padT} width={slot} height={plotH} fill="#f0fdfa" rx={3} />}
            <rect x={cx - barW / 2} y={baseY - h} width={barW} height={h} rx={3} fill={d.color ?? "#0f766e"} />
            <text x={cx} y={baseY - h - 5} textAnchor="middle" className="fill-slate-600 text-[10px] font-semibold tabular-nums">{kFmt(d.value)}</text>
            {d.delta && (
              <text x={cx} y={baseY - h - 16} textAnchor="middle" className="text-[9px] font-semibold tabular-nums" fill={deltaColor(d.deltaTone)}>{d.delta}</text>
            )}
            <text x={cx} y={height - 13} textAnchor="middle" className="fill-slate-500 text-[10px] font-medium">{d.label}</text>
            {d.sub && <text x={cx} y={height - 2} textAnchor="middle" className="fill-slate-400 text-[9px]">{d.sub}</text>}
          </g>
        );
      })}
      {showLine && (
        <>
          <polyline points={data.map((d, i) => `${cxOf(i)},${baseY - scale(d.value)}`).join(" ")} fill="none" stroke="#475569" strokeWidth={1.6} strokeDasharray="4 3" opacity={0.7} />
          {data.map((d, i) => (
            <circle key={i} cx={cxOf(i)} cy={baseY - scale(d.value)} r={2.5} fill="#475569" opacity={0.8} />
          ))}
        </>
      )}
    </svg>
  );
}

/** Stacked vertical bars — one bar per group, segments per series. */
export function StackedBars({
  groups,
  series,
  height = 200,
}: {
  groups: Array<{ label: string; values: number[]; sub?: string }>;
  series: Array<{ label: string; color: string }>;
  height?: number;
}) {
  const W = 1120;
  const padL = 8;
  const padR = 8;
  const padT = 14;
  const padB = 30;
  const plotW = W - padL - padR;
  const plotH = height - padT - padB;
  const totals = groups.map((g) => g.values.reduce((s, v) => s + v, 0));
  const max = Math.max(1, ...totals);
  const slot = plotW / Math.max(1, groups.length);
  const barW = Math.min(80, slot * 0.6);
  const baseY = padT + plotH;
  const scale = (v: number) => (v / max) * plotH;
  return (
    <svg viewBox={`0 0 ${W} ${height}`} className="w-full" role="img" aria-label="stacked bar chart">
      {[0.25, 0.5, 0.75, 1].map((f) => (
        <line key={f} x1={padL} y1={padT + plotH * (1 - f)} x2={W - padR} y2={padT + plotH * (1 - f)} stroke="#f1f5f9" />
      ))}
      <text x={padL} y={padT - 2} className="fill-slate-300 text-[9px] tabular-nums">{kFmt(max)}</text>
      <line x1={padL} y1={baseY} x2={W - padR} y2={baseY} stroke="#cbd5e1" />
      {groups.map((g, i) => {
        const cx = padL + i * slot + slot / 2;
        let y = baseY;
        return (
          <g key={i}>
            {g.values.map((v, si) => {
              const h = scale(v);
              y -= h;
              return <rect key={si} x={cx - barW / 2} y={y} width={barW} height={h} fill={series[si]?.color ?? "#94a3b8"} />;
            })}
            <text x={cx} y={baseY - scale(totals[i]) - 4} textAnchor="middle" className="fill-slate-600 text-[10px] font-semibold tabular-nums">{kFmt(totals[i])}</text>
            <text x={cx} y={height - 13} textAnchor="middle" className="fill-slate-500 text-[10px] font-medium">{g.label}</text>
            {g.sub && <text x={cx} y={height - 2} textAnchor="middle" className="fill-slate-400 text-[9px]">{g.sub}</text>}
          </g>
        );
      })}
    </svg>
  );
}

/** Ranked horizontal bars (HTML). Each item draws a value bar; optional util tone. */
export function HBarList({
  items,
  max,
  unit = "",
}: {
  items: Array<{ label: string; value: number; color?: string; right?: ReactNode; tone?: "ok" | "warn" | "bad" }>;
  max?: number;
  unit?: string;
}) {
  const m = max ?? Math.max(1, ...items.map((i) => i.value));
  const toneBar = (t?: string) => (t === "bad" ? "#dc2626" : t === "warn" ? "#d97706" : undefined);
  return (
    <div className="space-y-1.5">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <div className="w-32 shrink-0 truncate text-slate-600" title={it.label}>{it.label}</div>
          <div className="relative h-4 flex-1 overflow-hidden rounded bg-slate-100">
            <div
              className="h-full rounded"
              style={{ width: `${Math.max(2, (it.value / m) * 100)}%`, backgroundColor: toneBar(it.tone) ?? it.color ?? "#0f766e" }}
            />
          </div>
          <div className="w-20 shrink-0 text-right tabular-nums font-medium text-slate-700">
            {full(it.value)}{unit}
          </div>
          {it.right != null && <div className="w-16 shrink-0 text-right tabular-nums text-slate-400">{it.right}</div>}
        </div>
      ))}
    </div>
  );
}

/** Donut for a few segments with a center total. */
export function Donut({
  segments,
  size = 150,
}: {
  segments: Array<{ label: string; value: number; color: string }>;
  size?: number;
}) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const r = size / 2;
  const stroke = size * 0.18;
  const rad = r - stroke / 2;
  const C = 2 * Math.PI * rad;
  let acc = 0;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label="donut">
      <g transform={`rotate(-90 ${r} ${r})`}>
        {segments.map((s, i) => {
          const frac = s.value / total;
          const dash = `${frac * C} ${C}`;
          const off = -acc * C;
          acc += frac;
          return (
            <circle key={i} cx={r} cy={r} r={rad} fill="none" stroke={s.color} strokeWidth={stroke} strokeDasharray={dash} strokeDashoffset={off} />
          );
        })}
      </g>
      <text x={r} y={r - 2} textAnchor="middle" className="fill-slate-800 text-[13px] font-semibold tabular-nums">{kFmt(total)}</text>
      <text x={r} y={r + 13} textAnchor="middle" className="fill-slate-400 text-[9px]">total</text>
    </svg>
  );
}

/**
 * Daily forecast BARS (teal) with an ACTUAL overlay LINE (dashed slate). Every day
 * is labelled with its weekday + date. Actual points may be null (line skips gaps).
 * Vertical separators mark each week (every 7 bars).
 */
export function DailyBarsLine({
  points,
  height = 250,
}: {
  points: Array<{ weekday: string; date: string; a: number; b: number | null }>;
  height?: number;
}) {
  const W = 1180;
  const padL = 10;
  const padR = 10;
  const padT = 16;
  const padB = 40;
  const plotW = W - padL - padR;
  const plotH = height - padT - padB;
  const n = Math.max(1, points.length);
  const max = Math.max(1, ...points.flatMap((p) => [p.a, p.b ?? 0]));
  const slot = plotW / n;
  const barW = Math.min(26, slot * 0.6);
  const baseY = padT + plotH;
  const cx = (i: number) => padL + i * slot + slot / 2;
  const y = (v: number) => baseY - (v / max) * plotH;
  const aPts = points.map((p, i) => ({ p, i })).filter((o) => o.p.b != null);
  const aLine = aPts.map((o) => `${cx(o.i)},${y(o.p.b as number)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${height}`} className="w-full" role="img" aria-label="daily forecast vs actual">
      {[0.25, 0.5, 0.75, 1].map((f) => (
        <line key={f} x1={padL} y1={padT + plotH * (1 - f)} x2={W - padR} y2={padT + plotH * (1 - f)} stroke="#f1f5f9" />
      ))}
      <text x={padL} y={padT - 4} className="fill-slate-300 text-[9px] tabular-nums">{kFmt(max)}</text>
      {/* week separators */}
      {points.map((_, i) => (i % 7 === 0 && i > 0 ? <line key={`s${i}`} x1={padL + i * slot} y1={padT} x2={padL + i * slot} y2={baseY} stroke="#e2e8f0" /> : null))}
      <line x1={padL} y1={baseY} x2={W - padR} y2={baseY} stroke="#cbd5e1" />
      {/* forecast bars + value labels */}
      {points.map((p, i) => (
        <g key={`b${i}`}>
          <rect x={cx(i) - barW / 2} y={y(p.a)} width={barW} height={baseY - y(p.a)} rx={2} fill="#0f766e" opacity={0.85} />
          <text x={cx(i)} y={y(p.a) - 3} textAnchor="middle" className="fill-slate-500 text-[7px] tabular-nums">{kFmt(p.a)}</text>
        </g>
      ))}
      {/* actual overlay line */}
      {aLine && <polyline points={aLine} fill="none" stroke="#64748b" strokeWidth={1.6} strokeDasharray="4 3" />}
      {aPts.map((o) => (
        <circle key={`d${o.i}`} cx={cx(o.i)} cy={y(o.p.b as number)} r={2} fill="#64748b" />
      ))}
      {/* per-day labels: weekday + date */}
      {points.map((p, i) => (
        <g key={`l${i}`}>
          <text x={cx(i)} y={height - 24} textAnchor="middle" className={`text-[8px] ${p.weekday === "Sat" ? "fill-slate-500 font-semibold" : "fill-slate-400"}`}>{p.weekday}</text>
          <text x={cx(i)} y={height - 14} textAnchor="middle" className="fill-slate-400 text-[7px] tabular-nums">{p.date}</text>
        </g>
      ))}
    </svg>
  );
}

/** 24-hour mini area/bars (e.g. volume availability profile). */
export function HourBars({ byHour, height = 120, color = "#0ea5e9" }: { byHour: number[]; height?: number; color?: string }) {
  const W = 1120;
  const padL = 8;
  const padR = 8;
  const padT = 12;
  const padB = 18;
  const plotW = W - padL - padR;
  const plotH = height - padT - padB;
  const max = Math.max(1, ...byHour);
  const slot = plotW / 24;
  const barW = slot * 0.7;
  const baseY = padT + plotH;
  return (
    <svg viewBox={`0 0 ${W} ${height}`} className="w-full" role="img" aria-label="hourly profile">
      {[0.5, 1].map((f) => (
        <line key={f} x1={padL} y1={padT + plotH * (1 - f)} x2={W - padR} y2={padT + plotH * (1 - f)} stroke="#f1f5f9" />
      ))}
      <text x={padL} y={padT - 1} className="fill-slate-300 text-[8px] tabular-nums">{kFmt(max)}</text>
      <line x1={padL} y1={baseY} x2={W - padR} y2={baseY} stroke="#cbd5e1" />
      {byHour.map((v, h) => {
        const bh = (v / max) * plotH;
        const cx = padL + h * slot + slot / 2;
        return (
          <g key={h}>
            <rect x={cx - barW / 2} y={baseY - bh} width={barW} height={bh} rx={1.5} fill={color} opacity={0.85} />
            {h % 3 === 0 && <text x={cx} y={height - 5} textAnchor="middle" className="fill-slate-400 text-[8px]">{String(h).padStart(2, "0")}</text>}
          </g>
        );
      })}
    </svg>
  );
}
